import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const Navbar = ({ isHome }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    navigate('/');
  };

  // Solid bar once scrolled, or always solid on non-home pages
  const isSolid = scrolled || !isHome;

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/products', label: 'Products' },
    { to: '/book-service', label: 'Book service' },
    { to: '/about', label: 'About' },
  ];

  const dashboardLink =
    user?.role === 'admin'
      ? { to: '/admin/engineer-requests', label: 'Admin dashboard' }
      : user?.role === 'engineer'
      ? { to: '/engineer/dashboard', label: 'Dashboard' }
      : user?.role === 'customer'
      ? { to: '/customer/dashboard', label: 'Dashboard' }
      : null;

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-200 ${
          isSolid
            ? 'bg-white border-b border-gray-100 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">

          {/* Brand */}
          <Link
            to="/"
            onClick={scrollToTop}
            className={`flex items-center gap-2 font-semibold text-lg transition-colors ${
              isSolid ? 'text-gray-900' : 'text-white'
            }`}
          >
            <span className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
              MV
            </span>
            MV Computers
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {user?.role !== 'engineer' &&
              navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={scrollToTop}
                  className={`text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                    isSolid
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            {user && user.role !== 'admin' && user.role !== 'engineer' && (
              <Link
                to="/apply-engineer"
                onClick={scrollToTop}
                className={`text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                  isSolid
                    ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Apply as engineer
              </Link>
            )}

            {/* Divider */}
            <div className={`w-px h-6 mx-2 ${isSolid ? 'bg-gray-200' : 'bg-white/20'}`} />

            {user ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen((v) => !v)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                    isSolid
                      ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      : 'bg-white/15 text-white hover:bg-white/25'
                  }`}
                >
                  {initials || (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </button>

                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-md py-1.5 z-50">
                    <p className="px-4 py-2 text-xs text-gray-400 border-b border-gray-50 truncate">
                      {user.name || user.email}
                    </p>
                    <Link
                      to="/profile"
                      onClick={() => { scrollToTop(); setProfileOpen(false); }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      Profile
                    </Link>
                    {dashboardLink && (
                      <Link
                        to={dashboardLink.to}
                        onClick={() => setProfileOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        {dashboardLink.label}
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-gray-50 transition"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                onClick={scrollToTop}
                className={`text-sm font-medium px-4 py-2 rounded-lg transition ${
                  isSolid
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-white text-blue-700 hover:bg-blue-50'
                }`}
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsOpen((v) => !v)}
            className={`md:hidden p-1.5 rounded-lg focus:outline-none ${isSolid ? 'text-gray-700' : 'text-white'}`}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Spacer so content isn't hidden under fixed nav */}
      <div className="h-16" />

      {/* Mobile menu */}
      <div className={`fixed inset-0 z-30 md:hidden ${isOpen ? 'block' : 'hidden'}`}>
        <div className="absolute inset-0 bg-black/40" onClick={() => setIsOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-white shadow-xl p-5 transform transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <span className="font-semibold text-gray-900">Menu</span>
            <button onClick={() => setIsOpen(false)} className="p-1 text-gray-400 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {user && (
            <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-100">
              <span className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-sm font-semibold">
                {initials}
              </span>
              <p className="text-sm font-medium text-gray-800 truncate">{user.name || user.email}</p>
            </div>
          )}

          <div className="flex flex-col gap-1">
            {user?.role !== 'engineer' &&
              navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => { scrollToTop(); setIsOpen(false); }}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  {link.label}
                </Link>
              ))}
            {user && user.role !== 'admin' && user.role !== 'engineer' && (
              <Link
                to="/apply-engineer"
                onClick={() => { scrollToTop(); setIsOpen(false); }}
                className="px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Apply as engineer
              </Link>
            )}

            <div className="h-px bg-gray-100 my-2" />

            {user ? (
              <>
                <Link
                  to="/profile"
                  onClick={() => { scrollToTop(); setIsOpen(false); }}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Profile
                </Link>
                {dashboardLink && (
                  <Link
                    to={dashboardLink.to}
                    onClick={() => setIsOpen(false)}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    {dashboardLink.label}
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  className="text-left px-3 py-2.5 rounded-lg text-sm font-medium text-orange-600 hover:bg-gray-50 transition"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => { scrollToTop(); setIsOpen(false); }}
                className="px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition text-center"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;